import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import CardTools, { ExpandModal } from "components/CardTools";
import { downloadCsv } from "utils/downloadCsv";
import { fmtDec } from "utils/format";

// ── Constants ─────────────────────────────────────────────────────────────────
const BASE_VAR    = 16.9;
const BASE_VOL    = 5.9;
const BASE_BETA   = 1.20;
const BASE_RAER   = 0.21;
const VAR_LIMIT   = 25.0;
const RAER_TARGET = 0.15;
const RAER_MAX    = 0.65;
const RISK_MAX    = VAR_LIMIT * 1.4;

const BADGE_STYLE = {
  'Private Equity':    { background: 'rgba(78,143,221,0.15)',  color: '#4e8fdd',  border: '1px solid rgba(78,143,221,0.2)' },
  'Private Credit':    { background: 'rgba(184,92,184,0.15)',  color: '#b87bc8',  border: '1px solid rgba(184,92,184,0.2)' },
  'Real Estate/REITs': { background: 'rgba(34,212,126,0.15)',  color: '#22d47e',  border: '1px solid rgba(34,212,126,0.2)' },
};

const MOCK_ALTS = [
  { id: 'BCX51', security_id: 'T0001', name: 'Apollo Debt BDC Offshore',  subclass: 'Private Credit',    corr: -0.05, index: 'Bloomberg US Agg', alt_exposure:  2_500_000 },
  { id: 'BDP71', security_id: 'T0002', name: 'Blackstone BXPE (TE)',       subclass: 'Private Equity',    corr:  0.65, index: 'S&P 500',          alt_exposure:  4_800_000 },
  { id: 'BCQ06', security_id: 'T0003', name: 'Blue Owl Cred Inc Corp',     subclass: 'Private Credit',    corr: -0.02, index: 'Bloomberg US Agg', alt_exposure:  3_200_000 },
  { id: 'BCJ30', security_id: 'T0004', name: 'Blackstone BCRED-O',         subclass: 'Private Credit',    corr:  0.05, index: 'Bloomberg US Agg', alt_exposure:  1_750_000 },
  { id: 'VNQ',   security_id: 'T0005', name: 'Vanguard Real Estate ETF',   subclass: 'Real Estate/REITs', corr:  0.72, index: 'FTSE NAREIT All',  alt_exposure:  6_100_000 },
];

const _DEFAULT_ORIGINAL = {
  var: BASE_VAR, vol: BASE_VOL, beta: BASE_BETA, raer: BASE_RAER,
  var_limit: VAR_LIMIT, raer_target: RAER_TARGET, raer_max: RAER_MAX, risk_max: RISK_MAX,
};

const BASE_CONC = [];

const ASSET_DATA = {
  rows: [
    { label: 'Equity',       mv: 46.3, var: 55.6, child: 'eq-sub'  },
    { label: 'Fixed Income', mv: 18.9, var: 12.4, child: 'fi-sub'  },
    { label: 'Money Market', mv: 14.7, var: 6.2,  child: 'mm-sub'  },
    { label: 'Alternatives', mv: 12.1, var: 16.8, child: 'alt-sub' },
    { label: 'Commodities',  mv: 5.0,  var: 7.2,  child: 'com-sub' },
    { label: 'Cash',         mv: 3.0,  var: 1.8,  child: null      },
  ],
};

const ASSET_DRILL = {
  'eq-sub':  { parent: 'all', parentLabel: 'All Assets', rows: [
    { label: 'Large Cap', mv: 52, var: 65, child: 'eq-large' },
    { label: 'Mid Cap',   mv: 22, var: 18, child: null },
    { label: 'Small Cap', mv: 14, var: 10, child: null },
    { label: 'Intl',      mv: 12, var: 7,  child: null },
  ]},
  'fi-sub':  { parent: 'all', parentLabel: 'All Assets', rows: [
    { label: 'Govt Bonds', mv: 38, var: 28, child: null },
    { label: 'Corp IG',    mv: 32, var: 30, child: null },
    { label: 'Corp HY',    mv: 18, var: 26, child: null },
    { label: 'MBS',        mv: 12, var: 16, child: null },
  ]},
  'mm-sub':  { parent: 'all', parentLabel: 'All Assets', rows: [
    { label: 'T-Bills', mv: 55, var: 30, child: null },
    { label: 'Repo',    mv: 30, var: 50, child: null },
    { label: 'MMF',     mv: 15, var: 20, child: null },
  ]},
  'alt-sub': { parent: 'all', parentLabel: 'All Assets', rows: [
    { label: 'Private Eq.',  mv: 45, var: 50, child: 'alt-pe' },
    { label: 'Hedge Funds',  mv: 35, var: 32, child: null },
    { label: 'Real Estate',  mv: 20, var: 18, child: null },
  ]},
  'com-sub': { parent: 'all', parentLabel: 'All Assets', rows: [
    { label: 'Energy',      mv: 42, var: 52, child: null },
    { label: 'Metals',      mv: 35, var: 30, child: null },
    { label: 'Agriculture', mv: 23, var: 18, child: null },
  ]},
  'eq-large': { parent: 'eq-sub', parentLabel: 'Equity', rows: [
    { label: 'AAPL',  mv: 14,   var: 13.5, child: null },
    { label: 'MSFT',  mv: 11,   var: 6.8,  child: null },
    { label: 'NVDA',  mv: 9.2,  var: 14.1, child: null },
    { label: 'AMZN',  mv: 7,    var: 7.2,  child: null },
    { label: 'GOOGL', mv: 7.5,  var: 3.8,  child: null },
    { label: 'Other', mv: 51.3, var: 54.6, child: null },
  ]},
  'alt-pe':  { parent: 'alt-sub', parentLabel: 'Alternatives', rows: [
    { label: 'KKR XII',   mv: 40, var: 45, child: null },
    { label: 'BX IX',     mv: 35, var: 38, child: null },
    { label: 'Apollo XI', mv: 25, var: 17, child: null },
  ]},
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function lerpCol(a, b, t) {
  const p = s => [parseInt(s.slice(1,3),16), parseInt(s.slice(3,5),16), parseInt(s.slice(5,7),16)];
  const [ar,ag,ab] = p(a), [br,bg,bb] = p(b);
  const h = v => Math.round(v).toString(16).padStart(2,'0');
  return '#' + h(ar+(br-ar)*t) + h(ag+(bg-ag)*t) + h(ab+(bb-ab)*t);
}

function buildArcPaths(min, max, colorFn, r=105, thickness=14, cx=140, cy=148) {
  const N=120, START=Math.PI, SWEEP=Math.PI, ri=r-thickness;
  return Array.from({ length: N }, (_, i) => {
    const t0=START+(i/N)*SWEEP, t1=START+((i+1)/N)*SWEEP;
    const v=min+(i/N)*(max-min);
    const x1o=cx+Math.cos(t0)*r,  y1o=cy+Math.sin(t0)*r;
    const x2o=cx+Math.cos(t1)*r,  y2o=cy+Math.sin(t1)*r;
    const x1i=cx+Math.cos(t0)*ri, y1i=cy+Math.sin(t0)*ri;
    const x2i=cx+Math.cos(t1)*ri, y2i=cy+Math.sin(t1)*ri;
    return {
      fill: colorFn(v),
      d: `M${x1i.toFixed(1)},${y1i.toFixed(1)} L${x1o.toFixed(1)},${y1o.toFixed(1)} A${r},${r} 0 0,1 ${x2o.toFixed(1)},${y2o.toFixed(1)} L${x2i.toFixed(1)},${y2i.toFixed(1)} A${ri},${ri} 0 0,0 ${x1i.toFixed(1)},${y1i.toFixed(1)} Z`,
    };
  });
}

function needlePt(min, max, val, len, cx=140, cy=148) {
  const angle = Math.PI + ((Math.min(Math.max(val,min),max)-min)/(max-min))*Math.PI;
  return { x2: cx+Math.cos(angle)*len, y2: cy+Math.sin(angle)*len };
}

function tickLine(min, max, val, outer=116, inner=88, cx=140, cy=148) {
  const a = Math.PI + ((val-min)/(max-min))*Math.PI;
  return {
    x1: cx+Math.cos(a)*outer, y1: cy+Math.sin(a)*outer,
    x2: cx+Math.cos(a)*inner, y2: cy+Math.sin(a)*inner,
    lx: cx+Math.cos(a)*143,   ly: cy+Math.sin(a)*143,
  };
}

// Returns scale factor and unit suffix based on the raw dollar VaR value.
// < 1.5 M → thousands (K); >= 1.5 M → millions (M)
function varScale(raw) {
  return raw < 1_500_000 ? { factor: 1_000, unit: 'K' } : { factor: 1_000_000, unit: 'M' };
}

function deltaClass(mod, orig) {
  if (mod > orig+0.005) return 'up';
  if (mod < orig-0.005) return 'down';
  return 'flat';
}

function getNodeLabel(key, assetData, assetDrill) {
  const inData = assetData.rows.find(r => r.child === key);
  if (inData) return inData.label;
  for (const d of Object.values(assetDrill)) {
    const found = d.rows.find(r => r.child === key);
    if (found) return found.label;
  }
  return key;
}

// ── HalfGauge ─────────────────────────────────────────────────────────────────

function HalfGauge({ arcPaths, needles, tick, axisLabels, readouts, title }) {
  return (
    <div style={{ width: '100%' }}>
      {title && (
        <div style={{ fontFamily: 'var(--dash-font-b)', fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--dash-muted)', marginBottom: '0.2rem', width: '100%', textAlign: 'left' }}>
          {title}
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <svg viewBox="0 0 280 162" style={{ display: 'block', width: '100%', maxWidth: 280, overflow: 'visible' }}>
          {arcPaths.map((p, i) => <path key={i} d={p.d} fill={p.fill} opacity="0.88" />)}
          {needles.map((n, i) => (
            <g key={i}>
              <line x1="140" y1="148" x2={n.x2.toFixed(2)} y2={n.y2.toFixed(2)}
                stroke={n.stroke} strokeWidth={n.sw} strokeLinecap="round" />
              <circle cx={n.x2.toFixed(2)} cy={n.y2.toFixed(2)} r={n.tr} fill={n.stroke} />
            </g>
          ))}
          <circle cx="140" cy="148" r="6" fill="var(--dash-surface)" stroke="rgba(255,255,255,0.3)" strokeWidth="1.5" />
          <circle cx="140" cy="148" r="2.5" fill="rgba(255,255,255,0.6)" />
          {tick && (
            <>
              <line x1={tick.x1.toFixed(1)} y1={tick.y1.toFixed(1)} x2={tick.x2.toFixed(1)} y2={tick.y2.toFixed(1)}
                stroke="rgba(255,255,255,0.5)" strokeWidth="1.5" strokeDasharray="4 3" />
              <text x={tick.lx.toFixed(1)} y={(tick.ly+3).toFixed(1)} fontSize="13" fill="rgba(255,255,255,0.45)" textAnchor="middle">{tick.label}</text>
            </>
          )}
          <text x="28"  y="152" fontSize="13" fill="rgba(255,255,255,0.3)" textAnchor="end"  >{axisLabels.lo}</text>
          <text x="252" y="152" fontSize="13" fill="rgba(255,255,255,0.3)" textAnchor="start">{axisLabels.hi}</text>
        </svg>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: '0.5rem', width: '100%', marginTop: '0.4rem' }}>
          {readouts.map(r => (
            <div key={r.label} style={{ textAlign: 'center' }}>
              <div style={{ fontFamily: 'var(--dash-font-b)', fontSize: '0.6rem', color: 'var(--dash-muted)', marginBottom: 2 }}>{r.label}</div>
              <div style={{ fontFamily: 'var(--dash-font-m)', fontSize: '0.9rem', fontWeight: 600, color: r.color }}>{r.value}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── GaugeCard ─────────────────────────────────────────────────────────────────

function GaugeCard({ orig, mod }) {
  const raerOrig   = +orig.raer.toFixed(2);
  const raerMod    = +mod.raer.toFixed(2);
  const raerMax    = +orig.raer_max;
  const raerTarget = +orig.raer_target;

  const sc       = varScale(orig.var);
  const varOrig  = +(orig.var       / sc.factor).toFixed(1);
  const varMod   = +(mod.var        / sc.factor).toFixed(1);
  const varLimit = +(orig.var_limit / sc.factor).toFixed(1);
  const riskMax  = +(orig.risk_max  / sc.factor).toFixed(1);

  // Rebuild arcs from live values so colors align with actual limits
  const raerArc = useMemo(() =>
    buildArcPaths(0, raerMax, v => {
      const p = v / raerMax, tp = raerTarget / raerMax;
      if (p < tp - 0.04) return lerpCol('#e05252', '#C9A84C', Math.min(1, p / (tp - 0.04)));
      if (p < tp + 0.04) return lerpCol('#C9A84C', '#22d47e', (p - (tp - 0.04)) / 0.08);
      return '#22d47e';
    }), [raerMax, raerTarget]);

  const riskArc = useMemo(() =>
    buildArcPaths(0, riskMax, v => {
      const p = v / riskMax;
      const pY = (varLimit * 0.95) / riskMax;
      const pR = (varLimit * 1.05) / riskMax;
      if (p <= pY) return lerpCol('#22d47e', '#C9A84C', Math.min(1, p / pY));
      if (p <= pR) return lerpCol('#C9A84C', '#e05252', (p - pY) / (pR - pY));
      return '#e05252';
    }), [riskMax, varLimit]);

  const raerNeedles = [
    { ...needlePt(0, raerMax, raerOrig, 72), stroke: '#4e8fdd', sw: 2, tr: 3.5 },
    { ...needlePt(0, raerMax, raerMod,  98), stroke: '#e05252', sw: 3, tr: 4.5 },
  ];
  const riskNeedles = [
    { ...needlePt(0, riskMax, varOrig, 72), stroke: '#4e8fdd', sw: 2, tr: 3.5 },
    { ...needlePt(0, riskMax, varMod,  98), stroke: '#e05252', sw: 3, tr: 4.5 },
  ];

  return (
    <div className="wif-card">
      <div className="wif-card-header">
        <div className="wif-card-title">Gauges</div>
        <div style={{ fontFamily: 'var(--dash-font-b)', fontSize: '0.48rem', color: 'var(--dash-muted2)' }}>Two needles — before → after</div>
      </div>
      <div style={{ padding: '0.5rem 1rem 0.25rem' }}>
        <HalfGauge
          title="Sharpe Ratio vs BMK"
          arcPaths={raerArc}
          needles={raerNeedles}
          tick={{ ...tickLine(0, raerMax, raerTarget), label: `Target ${fmtDec(raerTarget)}` }}
          axisLabels={{ lo: '0', hi: fmtDec(raerMax) }}
          readouts={[
            { label: 'Before', value: fmtDec(raerOrig), color: '#4e8fdd' },
            { label: 'After',  value: fmtDec(raerMod),  color: '#e05252' },
          ]}
        />
      </div>
      <div style={{ borderTop: '1px solid var(--dash-border)', padding: '0.5rem 1rem 0.75rem' }}>
        <HalfGauge
          title="Risk vs Limit"
          arcPaths={riskArc}
          needles={riskNeedles}
          tick={{ ...tickLine(0, riskMax, varLimit), label: `Limit ${varLimit.toFixed(1)}${sc.unit}` }}
          axisLabels={{ lo: '0', hi: riskMax.toFixed(1) + sc.unit }}
          readouts={[
            { label: 'Before', value: varOrig + sc.unit, color: '#4e8fdd' },
            { label: 'After',  value: varMod  + sc.unit, color: +varMod > +varOrig ? '#e05252' : '#22d47e' },
          ]}
        />
      </div>
    </div>
  );
}

// ── AllocVarCanvas ────────────────────────────────────────────────────────────

function AllocVarCanvas({ altVarFactor, assetData, assetDrill, modAlloc }) {
  const [chartKey, setChartKey] = useState('all');
  const canvasRef  = useRef(null);
  const wrapRef    = useRef(null);
  const rowRectsRef = useRef([]);
  const chartHRef  = useRef(0);

  useEffect(() => { setChartKey('all'); }, [modAlloc]);

  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    const wrap   = wrapRef.current;
    if (!canvas || !wrap) return;
    const W = wrap.getBoundingClientRect().width;
    if (!W) return;

    const curData   = chartKey === 'all' ? assetData : assetDrill[chartKey];
    const modSource = modAlloc
      ? (chartKey === 'all' ? modAlloc.data : modAlloc.drill?.[chartKey])
      : null;
    const modMap = modSource?.rows
      ? Object.fromEntries(modSource.rows.map(r => [r.label, r]))
      : null;

    const rows = (curData?.rows || []).map(row => {
      const modRow = modMap?.[row.label] ?? null;
      const mvMod  = modRow ? modRow.mv : row.mv;
      let varMod;
      if (modRow) {
        varMod = modRow.var;
      } else {
        const altScaled = (chartKey === 'all' && row.label === 'Alternatives')
                       || chartKey === 'alt-sub' || chartKey === 'alt-pe';
        varMod = altScaled ? +(row.var * altVarFactor).toFixed(1) : row.var;
      }
      return {
        label:    row.label,
        mv:       row.mv,
        mvMod,
        varOrig:  row.var,
        varMod,
        drillable: !!row.child && !!assetDrill[row.child],
        child:    row.child,
      };
    });

    const dpr = window.devicePixelRatio || 1;
    const barH = 20, barGap = 4, groupGap = 14;
    const labelW = 140, padTop = 6, axisH = 32, padBot = 4;
    const groupH = barH * 2 + barGap;
    const H = padTop + rows.length * (groupH + groupGap) - groupGap + axisH + padBot;

    canvas.width  = W * dpr; canvas.height = H * dpr;
    canvas.style.width  = W + 'px';
    canvas.style.height = H + 'px';
    canvas.style.display = 'block';
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, W, H);

    const trackW = W - labelW - 8;
    const maxVal = Math.max(...rows.map(r => Math.max(r.mv + r.varOrig, r.mvMod + r.varMod))) * 1.05;

    rowRectsRef.current = [];
    chartHRef.current   = H;

    rows.forEach((row, i) => {
      const groupY = padTop + i * (groupH + groupGap);
      const cy = groupY + groupH / 2;

      // Label
      ctx.font = '400 12px Outfit, sans-serif';
      ctx.fillStyle = 'rgba(255,255,255,0.88)';
      ctx.textAlign = 'right'; ctx.textBaseline = 'middle';
      ctx.fillText(row.label, labelW - 8, cy);
      if (row.drillable) {
        ctx.font = '500 11px Outfit, sans-serif';
        ctx.fillStyle = '#4e8fdd';
        ctx.fillText('›', labelW - 1, cy + 0.5);
      }

      // Before bar
      const yO = groupY, cyO = yO + barH / 2;
      const mvOrigPx  = (row.mv      / maxVal) * trackW;
      const varOrigPx = (row.varOrig / maxVal) * trackW;
      ctx.fillStyle = 'rgba(255,255,255,0.04)';
      ctx.beginPath(); ctx.roundRect(labelW, yO, trackW, barH, 3); ctx.fill();
      ctx.fillStyle = '#1e3a5f';
      ctx.beginPath(); ctx.roundRect(labelW, yO, mvOrigPx, barH, varOrigPx > 0 ? [3,0,0,3] : 3); ctx.fill();
      if (varOrigPx > 0) {
        ctx.fillStyle = '#7a5a1a';
        ctx.beginPath(); ctx.roundRect(labelW + mvOrigPx, yO, varOrigPx, barH, [0,3,3,0]); ctx.fill();
      }
      ctx.font = '500 9px Outfit, sans-serif'; ctx.fillStyle = 'rgba(255,255,255,0.3)';
      ctx.textAlign = 'right'; ctx.textBaseline = 'middle';
      ctx.fillText('Before', labelW - 8, cyO);
      ctx.font = '500 9px DM Mono, monospace';
      if (mvOrigPx  >= 28) { ctx.fillStyle = 'rgba(255,255,255,0.55)'; ctx.textAlign = 'center'; ctx.fillText(fmtDec(row.mv) + '%', labelW + mvOrigPx/2, cyO); }
      if (varOrigPx >= 28) { ctx.fillStyle = 'rgba(255,255,255,0.55)'; ctx.textAlign = 'center'; ctx.fillText(fmtDec(row.varOrig) + '%', labelW + mvOrigPx + varOrigPx/2, cyO); }

      // After bar
      const yM = groupY + barH + barGap, cyM = yM + barH / 2;
      const mvPx  = (row.mvMod  / maxVal) * trackW;
      const varPx = (row.varMod / maxVal) * trackW;
      ctx.fillStyle = 'rgba(255,255,255,0.05)';
      ctx.beginPath(); ctx.roundRect(labelW, yM, trackW, barH, 3); ctx.fill();
      ctx.fillStyle = '#1e4a7a';
      ctx.beginPath(); ctx.roundRect(labelW, yM, mvPx, barH, varPx > 0 ? [3,0,0,3] : 3); ctx.fill();
      if (varPx > 0) {
        ctx.fillStyle = '#b8892a';
        ctx.beginPath(); ctx.roundRect(labelW + mvPx, yM, varPx, barH, [0,3,3,0]); ctx.fill();
      }
      ctx.font = '500 9px Outfit, sans-serif'; ctx.fillStyle = 'rgba(255,255,255,0.3)';
      ctx.textAlign = 'right'; ctx.textBaseline = 'middle';
      ctx.fillText('After', labelW - 8, cyM);
      ctx.font = '500 9px DM Mono, monospace';
      if (mvPx >= 28)       { ctx.fillStyle = 'rgba(255,255,255,0.92)'; ctx.textAlign = 'center'; ctx.fillText(fmtDec(row.mvMod) + '%', labelW + mvPx/2, cyM); }
      else                  { ctx.fillStyle = 'rgba(160,190,255,0.75)'; ctx.textAlign = 'left'; ctx.fillText(fmtDec(row.mvMod) + '%', labelW + mvPx + 3, cyM - 5); }
      if (varPx >= 28)      { ctx.fillStyle = 'rgba(255,255,255,0.92)'; ctx.textAlign = 'center'; ctx.fillText(fmtDec(row.varMod) + '%', labelW + mvPx + varPx/2, cyM); }
      else if (varPx > 0)   { ctx.fillStyle = 'rgba(220,185,110,0.85)'; ctx.textAlign = 'left'; ctx.fillText(fmtDec(row.varMod) + '%', labelW + mvPx + varPx + 3, cyM + 5); }

      rowRectsRef.current.push({ y: groupY, h: groupH, drillable: row.drillable, child: row.child });
    });

    // X-axis
    const axisY = H - axisH + 4;
    ctx.strokeStyle = 'rgba(255,255,255,0.05)'; ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(labelW, axisY); ctx.lineTo(labelW + trackW, axisY); ctx.stroke();
    ctx.font = '400 9px DM Mono, monospace'; ctx.textAlign = 'center'; ctx.textBaseline = 'top';
    for (let t = 0; t <= 5; t++) {
      const px = labelW + (t/5)*trackW;
      ctx.strokeStyle = 'rgba(255,255,255,0.3)'; ctx.lineWidth = 0.75;
      ctx.beginPath(); ctx.moveTo(px, axisY); ctx.lineTo(px, axisY+3); ctx.stroke();
      ctx.fillStyle = 'rgba(255,255,255,0.3)';
      ctx.fillText(Math.round((t/5)*maxVal) + '%', px, axisY+5);
    }

    // Legend
    const ly = H - axisH + 16;
    let lx = labelW;
    [{ col: '#1e4a7a', label: 'Allocation' }, { col: '#b8892a', label: 'VaR Contribution' }].forEach(item => {
      ctx.fillStyle = item.col;
      ctx.beginPath(); ctx.roundRect(lx, ly, 14, 7, 2); ctx.fill();
      ctx.font = '500 9px Outfit'; ctx.fillStyle = 'rgba(255,255,255,0.45)';
      ctx.textAlign = 'left'; ctx.textBaseline = 'middle';
      const tw = ctx.measureText(item.label).width;
      ctx.fillText(item.label, lx+18, ly+3.5);
      lx += 18 + tw + 14;
    });

    wrap.style.height = H + 'px';
  }, [chartKey, altVarFactor, assetData, assetDrill, modAlloc]);

  useEffect(() => { const t = setTimeout(draw, 30); return () => clearTimeout(t); }, [draw]);
  useEffect(() => { window.addEventListener('resize', draw); return () => window.removeEventListener('resize', draw); }, [draw]);

  function handleClick(e) {
    const canvas = canvasRef.current;
    if (!canvas || !chartHRef.current) return;
    const rect = canvas.getBoundingClientRect();
    const my = (e.clientY - rect.top) * (chartHRef.current / rect.height);
    for (const r of rowRectsRef.current) {
      if (my >= r.y && my <= r.y + r.h && r.drillable && r.child) {
        setChartKey(r.child); return;
      }
    }
  }

  function handleMouseMove(e) {
    const canvas = canvasRef.current;
    if (!canvas || !chartHRef.current) return;
    const rect = canvas.getBoundingClientRect();
    const my = (e.clientY - rect.top) * (chartHRef.current / rect.height);
    canvas.style.cursor = rowRectsRef.current.some(r => my >= r.y && my <= r.y + r.h && r.drillable) ? 'pointer' : 'default';
  }

  const drillInfo = chartKey !== 'all' ? assetDrill[chartKey] : null;
  const subtitle  = chartKey === 'all'
    ? 'Click an asset class to drill down'
    : (assetDrill[chartKey]?.rows?.some(r => r.child && assetDrill[r.child]) ? 'Click to drill further' : 'Leaf level');

  return (
    <div className="wif-card">
      <div className="wif-card-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
          <div className="wif-card-title" style={{ flexShrink: 0 }}>Asset Allocation vs VaR Contribution</div>
          {drillInfo && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 3, fontFamily: 'var(--dash-font-m)', fontSize: '0.5rem', color: 'var(--dash-muted2)' }}>
              <span style={{ color: '#4e8fdd', cursor: 'pointer' }} onClick={() => setChartKey('all')}>All</span>
              {drillInfo.parent !== 'all' && (
                <>
                  <span style={{ margin: '0 2px' }}>›</span>
                  <span style={{ color: '#4e8fdd', cursor: 'pointer' }} onClick={() => setChartKey(drillInfo.parent)}>{drillInfo.parentLabel}</span>
                </>
              )}
              <span style={{ margin: '0 2px' }}>›</span>
              <span style={{ color: 'var(--dash-text2)' }}>{getNodeLabel(chartKey, assetData, assetDrill)}</span>
            </div>
          )}
        </div>
        <div style={{ fontFamily: 'var(--dash-font-b)', fontSize: '0.48rem', color: '#4e8fdd', flexShrink: 0 }}>{subtitle}</div>
      </div>
      <div ref={wrapRef} style={{ position: 'relative', width: '100%' }}>
        <canvas ref={canvasRef} onClick={handleClick} onMouseMove={handleMouseMove} />
      </div>
    </div>
  );
}

// ── Risk bar chart ────────────────────────────────────────────────────────────

function RiskBarCanvas({ orig, mod }) {
  const ref    = useRef(null);
  const wrapRef = useRef(null);

  const draw = useCallback(() => {
    const canvas = ref.current, wrap = wrapRef.current;
    if (!canvas || !wrap) return;

    const sc       = varScale(orig.var);
    const varOrig  = +(orig.var / sc.factor).toFixed(1);
    const varMod   = +(mod.var  / sc.factor).toFixed(1);
    const volOrig  = +orig.vol.toFixed(1);
    const volMod   = +mod.vol.toFixed(1);
    const betaOrig = +orig.beta.toFixed(2);
    const betaMod  = +mod.beta.toFixed(2);

    const METRICS = [
      { label: 'VaR 95% 1D', origVal: +varOrig, modVal: +varMod, fmt: v => v.toFixed(1)+sc.unit, maxVal: Math.max(+varOrig, +varMod)*1.3 },
      { label: 'Volatility',  origVal: volOrig,  modVal: volMod,  fmt: v => fmtDec(v)+'%', maxVal: Math.max(volOrig,  volMod)  *1.3 },
      { label: 'Beta',        origVal: betaOrig, modVal: betaMod, fmt: v => fmtDec(v),      maxVal: Math.max(betaOrig, betaMod) *1.3 },
    ];

    const dpr = window.devicePixelRatio || 1;
    const W = wrap.getBoundingClientRect().width || 400;
    const H = 140;
    canvas.width = W*dpr; canvas.height = H*dpr;
    canvas.style.width = W+'px'; canvas.style.height = H+'px';
    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr); ctx.clearRect(0, 0, W, H);

    const n=METRICS.length, padL=12, padR=12, padTop=32, padBot=22;
    const groupW=(W-padL-padR)/n, barW=Math.min(28,groupW*0.28), gap=barW*0.5, chartH=H-padTop-padBot;

    METRICS.forEach((m, gi) => {
      const gx=padL+gi*groupW+groupW/2, bx=gx-gap/2-barW, ax=gx+gap/2, base=H-padBot;
      const worse = m.modVal > m.origVal;
      ctx.font='600 9px Outfit,sans-serif'; ctx.fillStyle='rgba(255,255,255,0.4)';
      ctx.textAlign='center'; ctx.textBaseline='top';
      ctx.fillText(m.label, gx, H-padBot+5);
      ctx.strokeStyle='rgba(255,255,255,0.07)'; ctx.lineWidth=1;
      ctx.beginPath(); ctx.moveTo(padL+gi*groupW,H-padBot); ctx.lineTo(padL+(gi+1)*groupW,H-padBot); ctx.stroke();
      const bH=(m.origVal/m.maxVal)*chartH;
      ctx.fillStyle='rgba(255,255,255,0.15)';
      ctx.beginPath(); ctx.roundRect(bx,base-bH,barW,bH,[3,3,0,0]); ctx.fill();
      const aH=(m.modVal/m.maxVal)*chartH;
      ctx.fillStyle=worse?'rgba(224,82,82,0.75)':'rgba(34,212,126,0.75)';
      ctx.beginPath(); ctx.roundRect(ax,base-aH,barW,aH,[3,3,0,0]); ctx.fill();
      ctx.font='500 9px DM Mono,monospace'; ctx.textBaseline='bottom';
      ctx.fillStyle='rgba(255,255,255,0.35)'; ctx.textAlign='center';
      ctx.fillText(m.fmt(m.origVal), bx+barW/2, base-bH-3);
      ctx.fillStyle=worse?'#e05252':'#22d47e';
      ctx.fillText(m.fmt(m.modVal),  ax+barW/2, base-aH-3);
    });

    const ly=8;
    ctx.fillStyle='rgba(255,255,255,0.18)'; ctx.beginPath(); ctx.roundRect(padL,ly,18,8,2); ctx.fill();
    ctx.font='500 8px Outfit'; ctx.fillStyle='rgba(255,255,255,0.4)'; ctx.textAlign='left'; ctx.textBaseline='middle';
    ctx.fillText('Before', padL+22, ly+4);
    const ax2=padL+72;
    ctx.fillStyle='#e05252'; ctx.beginPath(); ctx.roundRect(ax2,   ly,9,8,[2,0,0,2]); ctx.fill();
    ctx.fillStyle='#22d47e'; ctx.beginPath(); ctx.roundRect(ax2+9, ly,9,8,[0,2,2,0]); ctx.fill();
    ctx.fillStyle='rgba(255,255,255,0.4)'; ctx.fillText('After', ax2+22, ly+4);
  }, [orig, mod]);

  useEffect(() => { draw(); window.addEventListener('resize', draw); return () => window.removeEventListener('resize', draw); }, [draw]);

  return (
    <div ref={wrapRef} style={{ position: 'relative', width: '100%', padding: '0.5rem 0 0.25rem' }}>
      <canvas ref={ref} />
    </div>
  );
}

// ── Dual-bar concentrations chart ────────────────────────────────────────────

const _CONC_MAX_LABEL = {
  'Asset Class': 'Max Class',
  'Region':      'Max Region',
  'Currency':    'Max Currency',
  'Industry':    'Max Industry',
  'Single Name': 'Max Name',
};

function ConcDualChart({ rows, modRows }) {
  const [tooltip, setTooltip] = useState(null);

  if (!rows || rows.length === 0) {
    return <div style={{ padding: '0 0.75rem 0.75rem', color: 'var(--dash-muted)', fontSize: '0.52rem' }}>No data</div>;
  }

  const all = [...rows, ...modRows];
  const MAX = Math.max(...all.map(d => Math.max(d.max_weight, d.limit_value))) * 1.15;

  function barColor(weight, limit) {
    const r = weight / (limit || 1);
    return r <= 0.85 ? '#22d47e' : r <= 1.05 ? '#C9A84C' : '#e05252';
  }

  return (
    <>
      <div style={{ padding: '0 0.75rem 0.75rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '0 0 0.5rem' }}>
          {[
            { sw: { background: 'rgba(255,255,255,0.18)', width: 18, height: 6, borderRadius: 2 }, label: 'Current' },
            { sw: { background: 'rgba(78,143,221,0.75)',  width: 18, height: 6, borderRadius: 2 }, label: 'Modified' },
            { sw: { background: '#22d47e', width: 1.5, height: 10 },                              label: 'Limit'    },
          ].map(({ sw, label }) => (
            <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 5, fontFamily: 'var(--dash-font-b)', fontSize: '0.46rem', color: 'var(--dash-muted2)' }}>
              <div style={sw} />{label}
            </div>
          ))}
        </div>
        {rows.map((d, i) => {
          const m  = modRows[i] || d;
          const wO = (d.max_weight   / MAX * 100).toFixed(2);
          const wM = (m.max_weight   / MAX * 100).toFixed(2);
          const lP = (d.limit_value  / MAX * 100).toFixed(2);
          const cO = barColor(d.max_weight, d.limit_value);
          const cM = barColor(m.max_weight, d.limit_value);
          return (
            <div key={d.category}
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0.2rem 0', cursor: 'default' }}
              onMouseEnter={e => { const r = e.currentTarget.getBoundingClientRect(); setTooltip({ d, m, x: r.left + r.width / 2, y: r.top }); }}
              onMouseLeave={() => setTooltip(null)}
            >
              <div style={{ fontFamily: 'var(--dash-font-b)', fontSize: '0.52rem', color: 'var(--dash-text2)', width: 78, flexShrink: 0 }}>
                {d.category}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ position: 'relative', height: 9, marginBottom: 3 }}>
                  <div style={{ position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.04)', borderRadius: 3 }} />
                  <div style={{ position: 'absolute', top: 0, left: 0, height: '100%', width: wO + '%', background: cO, opacity: 0.3, borderRadius: 3 }} />
                  <div style={{ position: 'absolute', top: 0, left: lP + '%', width: 1.5, height: '100%', background: '#22d47e', opacity: 0.7, transform: 'translateX(-50%)' }} />
                </div>
                <div style={{ position: 'relative', height: 9 }}>
                  <div style={{ position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.04)', borderRadius: 3 }} />
                  <div style={{ position: 'absolute', top: 0, left: 0, height: '100%', width: wM + '%', background: '#4e8fdd', opacity: 0.75, borderRadius: 3 }} />
                  <div style={{ position: 'absolute', top: 0, left: lP + '%', width: 1.5, height: '100%', background: '#22d47e', opacity: 0.7, transform: 'translateX(-50%)' }} />
                </div>
              </div>
              <div style={{ width: 52, flexShrink: 0, textAlign: 'right' }}>
                <div style={{ fontFamily: 'var(--dash-font-m)', fontSize: '0.58rem', fontWeight: 600, color: cM }}>{fmtDec(m.max_weight)}%</div>
                <div style={{ fontFamily: 'var(--dash-font-m)', fontSize: '0.48rem', color: 'var(--dash-muted2)' }}>{fmtDec(d.max_weight)}%</div>
              </div>
            </div>
          );
        })}
      </div>

      {tooltip && (() => {
        const { d, m } = tooltip;
        const maxLabel = _CONC_MAX_LABEL[d.category] || 'Max';
        return (
          <div style={{
            position: 'fixed', pointerEvents: 'none', zIndex: 1200,
            left: tooltip.x, top: tooltip.y - 8,
            transform: 'translate(-50%, -100%)',
            background: 'rgba(8,14,24,0.96)',
            border: '1px solid rgba(255,255,255,0.10)',
            borderRadius: 6, padding: '6px 10px',
            fontFamily: 'var(--dash-font-m)', fontSize: '0.6rem',
            color: 'rgba(255,255,255,0.85)', whiteSpace: 'nowrap',
            boxShadow: '0 4px 16px rgba(0,0,0,0.45)',
          }}>
            <div style={{ fontFamily: 'var(--dash-font-b)', fontSize: '0.5rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--dash-muted)', marginBottom: 4 }}>
              {d.category}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {d.category_name && (
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}>
                  <span style={{ color: 'var(--dash-muted)' }}>{maxLabel}</span>
                  <span style={{ color: 'rgba(255,255,255,0.9)' }}>{d.category_name}</span>
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}>
                <span style={{ color: 'var(--dash-muted)' }}>Current</span>
                <span style={{ color: 'rgba(255,255,255,0.9)' }}>{fmtDec(d.max_weight)}%</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}>
                <span style={{ color: 'var(--dash-muted)' }}>Modified</span>
                <span style={{ color: 'rgba(255,255,255,0.9)' }}>{fmtDec(m.max_weight)}%</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16 }}>
                <span style={{ color: 'var(--dash-muted)' }}>Limit</span>
                <span style={{ color: 'rgba(255,255,255,0.9)' }}>{d.limit_value != null ? fmtDec(d.limit_value) + '%' : '—'}</span>
              </div>
            </div>
          </div>
        );
      })()}
    </>
  );
}

// ── Metric strip ──────────────────────────────────────────────────────────────

function MetricStrip({ orig, mod }) {
  const sc       = varScale(orig.var);
  const varOrig  = +(orig.var / sc.factor).toFixed(1);
  const varMod   = +(mod.var  / sc.factor).toFixed(1);
  const volOrig  = +orig.vol.toFixed(1);
  const volMod   = +mod.vol.toFixed(1);
  const betaOrig = +orig.beta.toFixed(2);
  const betaMod  = +mod.beta.toFixed(2);

  const items = [
    { label: 'VaR 95% 1D', orig: varOrig + sc.unit, mod: varMod + sc.unit, dc: deltaClass(+varMod, +varOrig) },
    { label: 'Volatility',  orig: fmtDec(volOrig) +'%',      mod: fmtDec(volMod) +'%',      dc: deltaClass(volMod,  volOrig)  },
    { label: 'Beta',        orig: fmtDec(betaOrig),        mod: fmtDec(betaMod),       dc: deltaClass(betaMod, betaOrig) },
  ];

  return (
    <div className="wif-rms-strip">
      {items.map(it => (
        <div key={it.label} className="wif-rms-item">
          <div className="wif-rms-label">{it.label}</div>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap' }}>
            <span className="wif-rms-val-orig">{it.orig}</span>
            <span className="wif-rms-val">{it.mod}</span>
            <span className={`wif-rms-delta ${it.dc}`}>{it.dc==='up'?'▲':it.dc==='down'?'▼':'—'}</span>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────

export default function AlternativesTab({ alts, panel, accountId }) {
  // ── original / modified metric state ──────────────────────────────────
  const [original, setOriginal] = useState(_DEFAULT_ORIGINAL);
  const [modified, setModified] = useState(_DEFAULT_ORIGINAL);

  useEffect(() => {
    if (panel?.original) {
      setOriginal(panel.original);
      setModified(panel.original);
      setModConc(null);
      setModAlloc(null);
    }
  }, [panel]);

  // ── Other panel data with fallbacks ───────────────────────────────────
  const baseConc   = panel?.concentrations ?? BASE_CONC;
  const assetData  = panel?.alloc?.data    ?? ASSET_DATA;
  const assetDrill = panel?.alloc?.drill   ?? ASSET_DRILL;

  // ── Positions state (table UI — not wired to modified in Phase 1) ─────
  const baseAlts = (alts && alts.length) ? alts : MOCK_ALTS;
  const _maxExp   = Math.max(...baseAlts.map(a => a.alt_exposure ?? 0), 0);
  const _expInM   = _maxExp >= 1_500_000;
  const expDiv    = _expInM ? 1_000_000 : 1_000;
  const expUnit   = _expInM ? 'M' : 'K';
  const expMax    = _expInM ? 100 : 1500;
  const expStep   = _expInM ? 0.5 : 10;
  const [adjAlts, setAdjAlts] = useState(MOCK_ALTS.map(a => ({ ...a })));

  useEffect(() => {
    const src = (alts && alts.length) ? alts : MOCK_ALTS;
    setAdjAlts(src.map(a => ({ ...a, corr: a.corr ?? a.corrEq ?? 0 })));
  }, [alts]);

  function handleField(i, field, raw) {
    const val = parseFloat(raw);
    if (isNaN(val)) return;
    setAdjAlts(prev => prev.map((a, idx) => idx === i ? { ...a, [field]: val } : a));
  }

  const [calculating, setCalculating] = useState(false);
  const [modConc, setModConc]         = useState(null);
  const [modAlloc, setModAlloc]       = useState(null);

  async function handleRecalculate() {
    const t = sessionStorage.getItem('token');
    setCalculating(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5050/api'}/whatif/alternatives/calculate?token=${t}${accountId ? `&account_id=${accountId}` : ''}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            positions: adjAlts.map(a => ({ security_id: a.security_id, corr: a.corr ?? 0, alt_exposure: a.alt_exposure ?? 0 })),
          }),
        }
      );
      const data = await res.json();
      if (data && !data.error) {
        setModified(prev => ({ ...prev, ...data }));
        if (Array.isArray(data.concentrations) && data.concentrations.length) {
          setModConc(data.concentrations);
        }
        if (data.alloc) {
          setModAlloc(data.alloc);
        }
      }
    } catch {}
    finally { setCalculating(false); }
  }

  const altVarFactor = original.var > 0 ? modified.var / original.var : 1;

  const [expandOpen, setExpandOpen] = useState(false);

  const handleCsv = () => downloadCsv(
    "alternatives_positions",
    [
      { label: "Security",             accessor: r => r.id },
      { label: "Name",                 accessor: r => r.name },
      { label: "Index",                accessor: r => r.index || "—" },
      { label: "Sub-class",            accessor: r => r.subclass },
      { label: "Correlation to Index", accessor: r => (r.corr * 100).toFixed(0) + "%" },
      { label: "Current Correlation",  accessor: r => fmtDec((baseAlts[r._i] || r).corr * 100) + "%" },
      { label: "Exposure",             accessor: r => (r.alt_exposure / expDiv).toFixed(_expInM ? 1 : 0) + expUnit },
      { label: "Current Exposure",     accessor: r => ((baseAlts[r._i] || r).alt_exposure / expDiv).toFixed(_expInM ? 1 : 0) + expUnit },
    ],
    adjAlts.map((a, _i) => ({ ...a, _i }))
  );

  const positionsTable = (
    <table className="wif-table">
      <thead>
        <tr>
          <th style={{ width: 80 }}>Security</th>
          <th>Name</th>
          <th>Index</th>
          <th>Sub-class</th>
          <th style={{ minWidth: 220, textAlign: 'left', padding: '0.5rem 1.2rem' }}>Correlation to Index</th>
          <th className="center" style={{ whiteSpace: 'nowrap' }}>Current Correlation</th>
          <th style={{ minWidth: 220, textAlign: 'left', padding: '0.5rem 1.2rem' }}>Exposure</th>
          <th className="center" style={{ whiteSpace: 'nowrap' }}>Current Exposure</th>
        </tr>
      </thead>
      <tbody>
        {adjAlts.map((alt, i) => {
          const orig  = baseAlts[i] || alt;
          const badge = BADGE_STYLE[alt.subclass] || { background: 'rgba(255,255,255,0.06)', color: 'var(--dash-muted)', border: '1px solid var(--dash-border)' };
          return (
            <tr key={alt.id}>
              <td style={{ whiteSpace: 'nowrap' }}>
                <div style={{ fontFamily: 'var(--dash-font-m)', fontSize: '0.62rem', fontWeight: 500 }}>{alt.id}</div>
              </td>
              <td>
                <div style={{ fontFamily: 'var(--dash-font-b)', fontSize: '0.52rem', color: 'var(--dash-text2)', whiteSpace: 'nowrap' }}>{alt.name}</div>
              </td>
              <td>
                <div style={{ fontFamily: 'var(--dash-font-b)', fontSize: '0.52rem', color: 'var(--dash-muted2)', whiteSpace: 'nowrap' }}>{alt.index || '—'}</div>
              </td>
              <td>
                <span style={{ ...badge, fontFamily: 'var(--dash-font-b)', fontSize: '0.48rem', fontWeight: 600, letterSpacing: '0.07em', padding: '0.15rem 0.55rem', borderRadius: 20, whiteSpace: 'nowrap', display: 'inline-block' }}>
                  {alt.subclass}
                </span>
              </td>
              <td style={{ padding: '0.5rem 1.2rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input type="range" className="wif-alt-slider" min="-1" max="1" step="0.01"
                    value={alt.corr} onChange={e => handleField(i, 'corr', e.target.value)} />
                  <input type="text" className="wif-alt-input"
                    value={(alt.corr * 100).toFixed(0) + '%'} onChange={e => handleField(i, 'corr', String(parseFloat(e.target.value) / 100))} />
                </div>
              </td>
              <td style={{ textAlign: 'center', padding: '0.5rem 0.85rem' }}>
                <span style={{ fontFamily: 'var(--dash-font-m)', fontSize: '0.72rem', color: 'var(--dash-muted2)' }}>
                  {fmtDec(orig.corr * 100)}%
                </span>
              </td>
              <td style={{ padding: '0.5rem 1.2rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input type="range" className="wif-alt-slider" min="0" max={expMax} step={expStep}
                    value={alt.alt_exposure / expDiv}
                    onChange={e => handleField(i, 'alt_exposure', String(parseFloat(e.target.value) * expDiv))} />
                  <input type="text" className="wif-alt-input"
                    value={(alt.alt_exposure / expDiv).toFixed(_expInM ? 1 : 0) + expUnit}
                    onChange={e => handleField(i, 'alt_exposure', String(parseFloat(e.target.value) * expDiv))} />
                </div>
              </td>
              <td style={{ textAlign: 'center', padding: '0.5rem 0.85rem' }}>
                <span style={{ fontFamily: 'var(--dash-font-m)', fontSize: '0.72rem', color: 'var(--dash-muted2)' }}>
                  {(orig.alt_exposure / expDiv).toFixed(_expInM ? 1 : 0)}{expUnit}
                </span>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  return (
    <>
      <div className="wif-page-hdr">
        <div>
          <div className="wif-page-title">Alternatives</div>
          <div className="wif-page-sub">Adjust correlations and capital call exposure per alternative position</div>
        </div>
      </div>

      {/* Positions table */}
      <div className="wif-card trg-tool-host">
        <CardTools onCsv={handleCsv} onExpand={() => setExpandOpen(true)} />
        <div className="wif-card-header">
          <div className="wif-card-title">Positions</div>
          <button
            className={`wif-add-btn${calculating ? ' disabled' : ''}`}
            onClick={handleRecalculate}
            disabled={calculating}
          >
            {calculating ? (
              <><span className="wif-spin">↻</span> Calculating…</>
            ) : (
              <>↻ Recalculate</>
            )}
          </button>
        </div>
        <div style={{ overflowX: 'auto' }}>
          {positionsTable}
        </div>
      </div>

      <ExpandModal open={expandOpen} title="Positions" onClose={() => setExpandOpen(false)} onCsv={handleCsv}>
        {positionsTable}
      </ExpandModal>

      <MetricStrip orig={original} mod={modified} />

      {/* Row 1: Risk bar chart | Concentrations */}
      <div className="wif-impact-grid">
        <div className="wif-card">
          <div className="wif-card-header">
            <div className="wif-card-title">Risk Measures — Impact of Changes</div>
            <div style={{ fontFamily: 'var(--dash-font-b)', fontSize: '0.5rem', color: 'var(--dash-muted2)' }}>Before vs after adjustments</div>
          </div>
          <RiskBarCanvas orig={original} mod={modified} />
        </div>
        <div className="wif-card">
          <div className="wif-card-header">
            <div className="wif-card-title">Concentrations vs Limits</div>
          </div>
          <ConcDualChart rows={baseConc} modRows={modConc ?? baseConc} />
        </div>
      </div>

      {/* Row 2: Gauges | Alloc vs VaR */}
      <div className="wif-impact-grid">
        <GaugeCard orig={original} mod={modified} />
        <AllocVarCanvas altVarFactor={altVarFactor} assetData={assetData} assetDrill={assetDrill} modAlloc={modAlloc} />
      </div>
    </>
  );
}
